import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ParcelTier } from '@/lib/delivery';

export interface CartItem {
  id: string;
  title: string;
  brand?: string;
  priceGBP: number;
  imageUrl?: string;
  parcelTier?: ParcelTier;
}

export type DeliveryOption = 'locker' | 'standard' | 'express';

interface CartState {
  items: CartItem[];
  /** Delivery method already chosen by the buyer (e.g. on the product page).
   *  When set, checkout skips asking again. */
  delivery: DeliveryOption | null;
  add: (item: CartItem, delivery?: DeliveryOption) => void;
  setDelivery: (d: DeliveryOption | null) => void;
  remove: (id: string) => void;
  clear: () => void;
  has: (id: string) => boolean;
}

/**
 * Shopping bag. Pre-loved items are unique, so there are no quantities - an item
 * is either in the cart or not. Persisted so it survives reloads.
 */
export const useCart = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      delivery: null,
      add: (item, delivery) =>
        set((s) => ({
          items: s.items.some((i) => i.id === item.id) ? s.items : [...s.items, item],
          // Remember the buyer's choice from the product page (first one wins).
          delivery: delivery ?? s.delivery,
        })),
      setDelivery: (d) => set({ delivery: d }),
      remove: (id) => set((s) => ({ items: s.items.filter((i) => i.id !== id) })),
      clear: () => set({ items: [], delivery: null }),
      has: (id) => get().items.some((i) => i.id === id),
    }),
    { name: 'souqli-cart' },
  ),
);

export const cartTotal = (items: CartItem[]) => items.reduce((s, i) => s + (i.priceGBP || 0), 0);
